import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Zap } from 'lucide-react'
import { tournamentsAPI } from '@/lib/api'

/* Track B · quick-setup wizard voor nieuwe tournaments.
   Usage:
     <TournamentSetupWizard onCreated={reloadList} />
   Rendert een acid-knop "Snel opzetten" die een dialog opent. */
export default function TournamentSetupWizard({ onCreated }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Zap className="h-4 w-4 mr-2 text-acid" />Snel opzetten
      </Button>
      {open && <WizardDialog open={open} onOpenChange={setOpen} onCreated={onCreated} />}
    </>
  )
}

function WizardDialog({ open, onOpenChange, onCreated }) {
  const [templates, setTemplates] = useState([])
  const [templateId, setTemplateId] = useState(null)
  const [name, setName] = useState('')
  const [start, setStart] = useState('')
  const [teamSize, setTeamSize] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    tournamentsAPI.listTemplates().then(r => {
      const list = r.data?.templates || []
      setTemplates(list)
      if (list[0]) { setTemplateId(list[0].id); setName(list[0].name); setTeamSize(list[0].team_size || '') }
    }).catch(() => {})
  }, [])

  const tpl = templates.find(t => t.id === templateId)

  const create = async () => {
    if (!templateId || !name) return
    setBusy(true)
    try {
      const r = await tournamentsAPI.createFromTemplate(templateId, {
        name, start_time: start || null,
        team_size: tpl?.registration_type === 'team' ? (teamSize ? Number(teamSize) : null) : null,
      })
      onOpenChange(false)
      onCreated?.()
      if (r.data?.id) window.location.href = `/tournaments/${r.data.id}`
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.error || e.message))
    } finally { setBusy(false) }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Snel opzetten</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              1. Kies template
            </div>
            <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
              {templates.map(t => (
                <button key={t.id} type="button"
                        onClick={() => { setTemplateId(t.id); setName(t.name); setTeamSize(t.team_size || '') }}
                        className={`text-left rounded-md border p-3 transition-colors ${
                          templateId === t.id
                            ? 'border-acid bg-acid/5'
                            : 'border-border hover:border-acid/50'
                        }`}>
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.description}</div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-2">
                    {t.type} · {t.registration_type} · {t.scoring_type}
                    {t.max_participants ? ` · max ${t.max_participants}` : ''}
                  </div>
                </button>
              ))}
            </div>
          </div>
          {tpl && (
            <>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  2. Naam
                </div>
                <input value={name} onChange={e => setName(e.target.value)}
                       className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
                       placeholder="Bijv. CS 1.6 Winter Cup" />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  3. Start (optioneel)
                </div>
                <input type="datetime-local" value={start} onChange={e => setStart(e.target.value)}
                       className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
              </div>
              {tpl.registration_type === 'team' && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    4. Spelers per team
                  </div>
                  <input type="number" min="1" value={teamSize}
                         onChange={e => setTeamSize(e.target.value)}
                         placeholder="Bv. 3 voor 3v3, 5 voor 5v5 — leeg = vrij"
                         className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
                  <p className="text-xs text-muted-foreground mt-1">
                    Als je hier een cijfer invult moet elke inschrijving exact dat aantal spelers uit het team kiezen.
                  </p>
                </div>
              )}
              {tpl.features && Object.keys(tpl.features).filter(k => tpl.features[k]).length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-mono text-[10px] uppercase tracking-widest">Features aan:</span>{' '}
                  {Object.entries(tpl.features).filter(([, v]) => v).map(([k]) => k).join(', ')}
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={create} disabled={busy || !templateId || !name}>Aanmaken</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
